import {MappedStammdatenAnteilseigner, NumberHelper, WithStammdatenIndex, WizardListPageBaseComponent} from '@utils-lib';
import {AnteilseignerPercentageService} from '../../service';
import {DecimalPipe} from '@angular/common';
import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {AnteilseignerAngaben, AnteilseignerDetail} from '@nachdigal';
import {Subscription} from 'rxjs';

@Component({
    selector: 'common-ui-anteilseigner-angaben',
    templateUrl: './anteilseigner-angaben.component.html',
    styleUrls: ['./anteilseigner-angaben.component.scss']
})
export class AnteilseignerAngabenComponent extends WizardListPageBaseComponent<AnteilseignerAngaben> implements OnInit, OnDestroy {

    static nutzdatenPath = 'anteilseignerAngaben';
    static route = 'anteilseigner-angaben';

    sumOfAnteilseignerPercentage: string;
    showPercantageAlert: boolean;
    showAlert: boolean;
    isRequired = true;

    public percentageSubscription: Subscription;

    constructor(protected readonly activatedRoute: ActivatedRoute,
                private readonly router: Router,
                private readonly translateService: TranslateService,
                private readonly anteilseignerPercentageService: AnteilseignerPercentageService,
                private readonly decimalPipe: DecimalPipe) {
        super(activatedRoute);
        this.maxAllowed = 99;
    }

    private static isNotGreaterThan100(value: number): boolean {
        return Math.fround(value) > Math.fround(100.0000000000);
    }

    private static isSmallerThan99(value: number): boolean {
        return Math.fround(value) < Math.fround(99.0000000000);
    }

    public ngOnInit(): void {
        super.ngOnInit();
        if (this.data.treuhand !== undefined) {
            this.isRequired = false;
        }
        const stammdatenLength = this.stammdaten?.unternehmen?.anteilseigner?.length;
        if (stammdatenLength > this.maxAllowed) {
            this.showMaxLimitHint = true;
        }
        if (!this.data.anteilseigner) {
            this.data.anteilseigner = [];
            const pageIsTouched = this.activatedRoute.parent?.snapshot.data.taskData.metadaten.pages.anteilseignerAngaben.touched;
            if (stammdatenLength !== 0 && !pageIsTouched) {
                for (let i = 0; i < stammdatenLength; i++) {
                    this.fillAnteilseignerFromStammdaten(this.stammdaten.unternehmen.anteilseigner[i]);
                    const addressMappingRules = new Map<string, unknown | Array<unknown>>([
                        ['inlandsadresse', this.stammdaten?.unternehmen.anteilseigner[i].value.inlandsadressen],
                        ['auslandsadresse', this.stammdaten?.unternehmen.anteilseigner[i].value.auslandsadressen]
                    ]);
                    this.stammdatenService.assignStammdatenIfDeterministic(addressMappingRules, this.data.anteilseigner[i]);
                }
            }
        }
        this.setSummeBeteiligterAnteilseigner();
        this.percentageSubscription = this.anteilseignerPercentageService.onPercentageChange.subscribe(() => {
            this.setSummeBeteiligterAnteilseigner();
        });
    }

    public setRequired($event: string) {
        this.isRequired = $event === '0: undefined';
    }

    public ngOnDestroy(): void {
        this.percentageSubscription.unsubscribe();
    }

    public addAnteilseigner(): void {
        this.data.anteilseigner.push({steuernummer: {}});
        this.selectedIndex = this.data.anteilseigner.length - 1;
    }

    public removeAnteilseigner(index: number): void {
        this.data.anteilseigner.splice(index, 1);
        this.setSummeBeteiligterAnteilseigner();
    }

    public getDescription(anteilseigner: AnteilseignerDetail): string {
        if (anteilseigner?.natuerlichePerson?.nachname && anteilseigner?.natuerlichePerson?.vorname) {
            return `${anteilseigner.natuerlichePerson.vorname} ${anteilseigner.natuerlichePerson.nachname}`;
        }
        if (anteilseigner?.juristischePerson?.firmenname) {
            return `${anteilseigner?.juristischePerson?.firmenname}`;
        }
        return this.translateService.instant('COMMON_UI.FRAGEBOGEN.ANTEILSEIGNER.NEW_ANTEILSEIGNER');
    }

    public getPercentage(anteilseigner: AnteilseignerDetail): string {
        const value = anteilseigner?.hoeheDerBeteiligung?.hoeheDerBeteiligungProzent;
        if (value) {
            return this.formatGermanNumber(value);
        }
        return '';
    }

    public setSummeBeteiligterAnteilseigner(): void {
        let sum = 0.0;
        let hoeheDerBeteiligungProzentExists = false;
        for (const current of this.data.anteilseigner) {
            if (current && current.hoeheDerBeteiligung) {
                const num = Number(current.hoeheDerBeteiligung.hoeheDerBeteiligungProzent);
                if (!isNaN(num)) {
                    hoeheDerBeteiligungProzentExists = true;
                    sum = sum + num;
                }
            }
        }
        this.showPercantageAlert = hoeheDerBeteiligungProzentExists && AnteilseignerAngabenComponent.isSmallerThan99(sum);
        this.showAlert = AnteilseignerAngabenComponent.isNotGreaterThan100(sum);
        this.sumOfAnteilseignerPercentage = this.formatGermanNumber(sum);
    }

    public onAnteilseignerPicked(anteilseigner: WithStammdatenIndex<MappedStammdatenAnteilseigner>): void {
        this.fillAnteilseignerFromStammdaten(anteilseigner);
        this.selectedIndex = this.data.anteilseigner.length - 1;
    }

    public fillAnteilseignerFromStammdaten(anteilseigner: WithStammdatenIndex<MappedStammdatenAnteilseigner>): void {
        const anteilseignerDetail = {
            laufendeNummer: anteilseigner.value.laufendeNummer,
            personenTyp: anteilseigner.value.personenTyp,
            natuerlichePerson: anteilseigner.value.natuerlichePerson,
            juristischePerson: anteilseigner.value.juristischePerson,
            weitereAngabenAnteilseigner: anteilseigner.value.weitereAngabenAnteilseigner,
            steuernummer: anteilseigner.value.steuernummer,
            hoeheDerBeteiligung: {
                hoeheDerBeteiligungNominell: anteilseigner.value.hoeheDerBeteiligung?.hoeheDerBeteiligungNominell,
                hoeheDerBeteiligungProzent:
                    NumberHelper.roundNumber(anteilseigner.value.hoeheDerBeteiligung?.hoeheDerBeteiligungProzent, 10000)
            },
            stammdatenIndex: anteilseigner.stammdatenIndex
        };
        this.data.anteilseigner.push(anteilseignerDetail);
    }

    protected getStammdatenMappingRules(): Map<string, unknown> {
        return new Map<string, unknown | Array<unknown>>();
    }

    private formatGermanNumber(nmber: number): string {
        try {
            return this.decimalPipe.transform(nmber, '.2-4', 'de-DE');
        } catch {
            return '';
        }
    }

}
